import _ from 'underscore';

import {
  ButtonType,
  ButtonWidget,
  MenuItemInfo
} from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { Formatters, Messages } from '../../../codegen/Localize';
import {
  ButtonControlledChildComponentProps,
  ButtonControlledComponent,
  ButtonProps
} from './ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu } from './ButtonControlledFlyoutListMenu';

export type SelectionMenuProps = {
  readonly selectionSize: number;
  readonly selectAll: () => void;
  readonly clear: () => void;
};

export class SelectionMenu extends React.PureComponent<SelectionMenuProps> {

  private readonly memoizedGetSelectionMenuButtonComponent = defaultMemoize(getSelectionMenuButtonComponent);
  private readonly memoizedGetSelectionMenuChildComponent = defaultMemoize(getSelectionMenuChildComponent);

  public render(): JSX.Element {
    const button = this.memoizedGetSelectionMenuButtonComponent(this.props.selectionSize);
    const child = this.memoizedGetSelectionMenuChildComponent(this.props.selectAll, this.props.clear);
    return (
      <span className={'tb-box'}>
        <ButtonControlledComponent
          buttonComponent={button}
          childComponent={child}
          testId={'selection-menu-test-id'}
        />
      </span>
    );
  }

}

type SelectionMenuButtonProps = ButtonProps & {
  readonly selectionSize: number;
};

function SelectionMenuButton(props: SelectionMenuButtonProps): JSX.Element {
  const selectionSize = props.selectionSize;
  const selectedItemsString = Messages.selectedItems({
    COUNT: selectionSize,
    COUNT_LOC: Formatters.formatCount(selectionSize)
  });
  return (
    <ButtonWidget
      buttonType={ButtonType.DarkText}
      handleClick={_.noop}
      children={`\u25be ${selectedItemsString}`}
      testId={'selection-menu-dropdown-button'}
    />
  );
}

function getSelectionMenuButtonComponent(
  selectionSize: number,
): React.ComponentType<ButtonProps> {
  return function buttonWrapper(buttonProps: ButtonProps): JSX.Element {
    const fullProps: SelectionMenuButtonProps = { ...buttonProps, selectionSize };
    return <SelectionMenuButton {...fullProps} />;
  };
}

function getSelectionMenuChildComponent(
  selectAll: () => void,
  clear: () => void,
): React.ComponentType<ButtonControlledChildComponentProps> {
  const menuItems: MenuItemInfo[] = [
    {
      displayValue: Messages.selectAll(),
      value: 'selectAll'
    },
    {
      displayValue: Messages.clear(),
      value: 'clear'
    }
  ];
  const itemSelected = (value: string) => {
    switch (value) {
      case 'selectAll':
        selectAll();
        break;
      case 'clear':
        clear();
        break;
    }
  };
  return function SelectionMenuWrapper(props: ButtonControlledChildComponentProps): JSX.Element {
    return <ButtonControlledFlyoutListMenu
      onComponentDidMount={_.noop}
      menuItems={menuItems}
      onMenuItemSelected={itemSelected}
      anchorElement={props.anchorElement}
      closeMe={props.closeMe}
      testId={'selection-menu-wrapper'}
    />;
  };
}
